import { Alert, Button, Card, Modal, Tooltip, Typography } from 'antd';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { api } from 'services/api';
import { Bitrix24Consumer } from 'contexts/legacy/Bitrix24Context';

const { Text } = Typography;

const PRIMARY = '#FF684A';
const PRIMARY_LIGHTER = '#FFE2DB';
const SUCCESS_LIGHTER = '#D3FCD2';
const SUCCESS_MAIN = '#43AC8D';
const WARNING_LIGHTER = '#FFF5CC';
const WARNING_MAIN = '#f78e2f';

function IconCircle({ bg, children }) {
	return (
		<div
			style={{
				width: 40,
				height: 40,
				borderRadius: '50%',
				backgroundColor: bg,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				flexShrink: 0,
			}}
		>
			{children}
		</div>
	);
}

function RoundedButton({
	children,
	variant = 'default',
	color,
	style,
	loading,
	disabled,
	onClick,
}) {
	const base = {
		borderRadius: 9999,
		fontWeight: 600,
		paddingLeft: 20,
		paddingRight: 20,
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		height: 36,
		fontSize: 14,
		border: 'none',
	};

	let specific = {};
	if (variant === 'contained' && color === 'primary') {
		specific = { backgroundColor: PRIMARY, color: '#fff', border: `1px solid ${PRIMARY}` };
	} else if (variant === 'contained' && color === 'success') {
		specific = {
			backgroundColor: SUCCESS_MAIN,
			color: '#fff',
			border: `1px solid ${SUCCESS_MAIN}`,
		};
	} else if (variant === 'outlined' || variant === 'default') {
		specific = { backgroundColor: 'transparent', color: '#595959', border: '1px solid #d9d9d9' };
	} else if (variant === 'outlined-success') {
		specific = {
			backgroundColor: 'transparent',
			color: SUCCESS_MAIN,
			border: `1px solid ${SUCCESS_MAIN}`,
		};
	}

	return (
		<Button
			loading={loading}
			disabled={disabled}
			onClick={onClick}
			style={{ ...base, ...specific, ...style }}
		>
			{children}
		</Button>
	);
}

const TransferIcon = ({ color = 'currentColor', size = 18 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
		<path
			d="M7 16H3v4m0 0l4-4M3 20l4-4M17 8h4V4m0 0l-4 4m4-4l-4 4M3 12a9 9 0 0 1 9-9 8.97 8.97 0 0 1 6.364 2.636M21 12a9 9 0 0 1-9 9 8.97 8.97 0 0 1-6.364-2.636"
			stroke={color}
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const BackIcon = ({ color = 'currentColor', size = 18 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
		<path
			d="M9 14L4 9l5-5M4 9h10a7 7 0 0 1 0 14H9"
			stroke={color}
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const InfoOutlineIcon = ({ color = '#bfbfbf', size = 16 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
		<circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
		<path d="M12 16v-4M12 8h.01" stroke={color} strokeWidth="2" strokeLinecap="round" />
	</svg>
);

const CheckCircleFillIcon = ({ color = PRIMARY, size = 16 }) => (
	<svg
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		style={{ flexShrink: 0, marginTop: 1 }}
	>
		<circle cx="12" cy="12" r="10" fill={color} opacity="0.2" />
		<path
			d="M7.5 12.5l3 3 6-6"
			stroke={color}
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const DangerIcon = ({ color = WARNING_MAIN, size = 32 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none">
		<path
			d="M12 2L2 21h20L12 2z"
			stroke={color}
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
		<path d="M12 9v4M12 17h.01" stroke={color} strokeWidth="2" strokeLinecap="round" />
	</svg>
);

const SuccessCircleIcon = ({ color = SUCCESS_MAIN, size = 32 }) => (
	<svg width={size} height={size} viewBox="0 0 24 24" fill="none">
		<circle cx="12" cy="12" r="10" stroke={color} strokeWidth="2" />
		<path
			d="M7.5 12.5l3 3 6-6"
			stroke={color}
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

function MigrateToKoryView({ bitrix24 }) {
	const intl = useIntl();
	const t = (id) => intl.formatMessage({ id });

	const [migrating, setMigrating] = useState(false);
	const [reverting, setReverting] = useState(false);
	const [showConfirmModal, setShowConfirmModal] = useState(false);
	const [showCompletionModal, setShowCompletionModal] = useState(false);
	const [revertError, setRevertError] = useState(null);

	const handleMigrate = async () => {
		setShowConfirmModal(false);
		try {
			setMigrating(true);
			await api.post('migrate-to-kory', {
				domain: bitrix24.infos.domain,
				auth: bitrix24.infos.auth,
			});
			setShowCompletionModal(true);
		} catch (error) {
			Modal.error({
				title: t('migrate_kory_error_title'),
				content: error?.response?.data?.message || error.message,
				okButtonProps: { style: { borderRadius: 9999 } },
			});
		} finally {
			setMigrating(false);
		}
	};

	const handleRevert = async () => {
		setRevertError(null);
		try {
			setReverting(true);
			await api.post('revert-from-kory', {
				domain: bitrix24.infos.domain,
				auth: bitrix24.infos.auth,
			});
			Modal.success({
				title: t('migrate_kory_revert_success_title'),
				content: t('migrate_kory_revert_success_content'),
				okButtonProps: { style: { borderRadius: 9999 } },
			});
		} catch (error) {
			setRevertError(error?.response?.data?.message || error.message);
		} finally {
			setReverting(false);
		}
	};

	return (
		<div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 24 }}>
			<Alert
				type="info"
				showIcon
				message={
					<Text strong style={{ fontSize: 13 }}>
						{t('migrate_kory_alert_title')}
					</Text>
				}
				description={t('migrate_kory_alert_description')}
				style={{ borderRadius: 12 }}
			/>

			<div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
				<Card
					style={{ flex: 1, minWidth: 260, borderRadius: 12 }}
					bodyStyle={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 20 }}
				>
					<div
						style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
					>
						<IconCircle bg={PRIMARY_LIGHTER}>
							<TransferIcon color={PRIMARY} size={20} />
						</IconCircle>
						<Tooltip title={t('migrate_kory_card_migrate_tooltip')} placement="top">
							<span style={{ cursor: 'default', padding: '4px', lineHeight: 1 }}>
								<InfoOutlineIcon size={16} />
							</span>
						</Tooltip>
					</div>

					<Text strong style={{ fontSize: 15 }}>
						{t('migrate_kory_card_migrate_title')}
					</Text>

					<Text type="secondary" style={{ fontSize: 13, flex: 1 }}>
						{t('migrate_kory_card_migrate_description')}
					</Text>

					<div>
						<RoundedButton
							variant="contained"
							color="primary"
							loading={migrating}
							disabled={migrating || reverting}
							onClick={() => setShowConfirmModal(true)}
						>
							<TransferIcon color="#fff" size={14} />
							{t('migrate_kory_card_migrate_button')}
						</RoundedButton>
					</div>
				</Card>

				<Card
					style={{ flex: 1, minWidth: 260, borderRadius: 12 }}
					bodyStyle={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 20 }}
				>
					<div
						style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}
					>
						<IconCircle bg="#f5f5f5">
							<BackIcon color="#8c8c8c" size={20} />
						</IconCircle>
						<Tooltip title={t('migrate_kory_card_revert_tooltip')} placement="top">
							<span style={{ cursor: 'default', padding: '4px', lineHeight: 1 }}>
								<InfoOutlineIcon size={16} />
							</span>
						</Tooltip>
					</div>

					<Text strong style={{ fontSize: 15 }}>
						{t('migrate_kory_card_revert_title')}
					</Text>

					<Text type="secondary" style={{ fontSize: 13, flex: 1 }}>
						{t('migrate_kory_card_revert_description')}
					</Text>

					<div>
						<RoundedButton
							variant="outlined"
							loading={reverting}
							disabled={reverting || migrating}
							onClick={handleRevert}
						>
							<BackIcon color="#595959" size={14} />
							{t('migrate_kory_card_revert_button')}
						</RoundedButton>
					</div>

					{revertError && (
						<Alert
							type="error"
							message={`Erro: ${revertError}`}
							showIcon
							style={{ borderRadius: 8, fontSize: 13 }}
						/>
					)}
				</Card>
			</div>

			<Modal
				visible={showConfirmModal}
				onCancel={() => setShowConfirmModal(false)}
				footer={null}
				zIndex={1050}
				width={400}
				bodyStyle={{ padding: '32px', textAlign: 'center' }}
			>
				<div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
					<div
						style={{
							width: 64,
							height: 64,
							borderRadius: 16,
							backgroundColor: WARNING_LIGHTER,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<DangerIcon />
					</div>
				</div>

				<Text strong style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
					{t('migrate_kory_confirm_title')}
				</Text>

				<Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 16 }}>
					{t('migrate_kory_confirm_description')}
				</Text>

				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 10,
						marginBottom: 24,
						textAlign: 'left',
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'flex-start',
							gap: 12,
							backgroundColor: '#fffbe6',
							border: '1px solid #ffe58f',
							borderRadius: 10,
							padding: '12px 14px',
						}}
					>
						<span style={{ fontSize: 18, lineHeight: 1 }}>📌</span>
						<Text style={{ fontSize: 13 }}>
							<Text strong>{t('migrate_kory_confirm_warning_title')}</Text>{' '}
							{t('migrate_kory_confirm_warning_text')}
						</Text>
					</div>
					<div
						style={{
							display: 'flex',
							alignItems: 'flex-start',
							gap: 12,
							backgroundColor: '#f6ffed',
							border: '1px solid #b7eb8f',
							borderRadius: 10,
							padding: '12px 14px',
						}}
					>
						<span style={{ fontSize: 18, lineHeight: 1 }}>✅</span>
						<Text style={{ fontSize: 13 }}>{t('migrate_kory_confirm_success_text')}</Text>
					</div>
				</div>

				<div style={{ display: 'flex', gap: 12 }}>
					<RoundedButton
						variant="outlined"
						style={{ flex: 1, justifyContent: 'center' }}
						onClick={() => setShowConfirmModal(false)}
					>
						{t('migrate_kory_confirm_cancel')}
					</RoundedButton>
					<RoundedButton
						variant="contained"
						color="primary"
						loading={migrating}
						style={{ flex: 1, justifyContent: 'center' }}
						onClick={handleMigrate}
					>
						<CheckCircleFillIcon color="#fff" size={14} />
						{t('migrate_kory_confirm_ok')}
					</RoundedButton>
				</div>
			</Modal>

			<Modal
				visible={showCompletionModal}
				onCancel={() => setShowCompletionModal(false)}
				footer={null}
				width={400}
				bodyStyle={{ padding: '32px', textAlign: 'center' }}
			>
				<div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
					<div
						style={{
							width: 64,
							height: 64,
							borderRadius: '50%',
							backgroundColor: SUCCESS_LIGHTER,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<SuccessCircleIcon />
					</div>
				</div>

				<Text strong style={{ fontSize: 16, display: 'block', marginBottom: 8 }}>
					{t('migrate_kory_success_title')}
				</Text>

				<Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 24 }}>
					{t('migrate_kory_success_description')}
				</Text>

				<RoundedButton
					variant="outlined-success"
					style={{ width: '100%', justifyContent: 'center' }}
					onClick={() => setShowCompletionModal(false)}
				>
					{t('migrate_kory_success_button')}
				</RoundedButton>
			</Modal>
		</div>
	);
}

export default Bitrix24Consumer(MigrateToKoryView);
